import { sha256 } from "@noble/hashes/sha2.js";
import { z } from "zod";
import type { TLRecord } from "@tldraw/tlschema";
import type { RecordStorage, Transaction } from "./sync/storage";
const token = z.string().regex(/^[a-f0-9]{64}$/);
export const preconditionsSchema = z
  .object({
    records: z.record(z.string(), token.nullable()),
    children: z.record(z.string(), token).optional(),
    pages: token.optional(),
  })
  .strict();
export type Preconditions = z.infer<typeof preconditionsSchema>;
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, canonical(v)]),
    );
  return value;
}
export function recordVersion(value: unknown) {
  return Array.from(
    sha256(new TextEncoder().encode(JSON.stringify(canonical(value)))),
  )
    .map((n) => n.toString(16).padStart(2, "0"))
    .join("");
}
function members(store: Record<string, TLRecord>, id: string) {
  const threads = Object.values(store)
    .filter(
      (r) =>
        r.typeName === "comment-thread" &&
        r.anchor.type === "shape" &&
        r.anchor.shapeId === id,
    )
    .map((r) => r.id);
  return Object.values(store)
    .filter(
      (r) =>
        (r.typeName === "shape" && r.parentId === id) ||
        (r.typeName === "comment-thread" && threads.includes(r.id)) ||
        (r.typeName === "comment" && threads.includes(r.threadId)),
    )
    .map((r) => r.id)
    .sort();
}
export function readPreconditions(
  store: Record<string, TLRecord>,
  ids: string[],
): Preconditions {
  const keep = new Set(ids);
  const add = (id: string, depth = 0) => {
    if (depth > 32) return;
    const r = store[id];
    if (!r) return;
    keep.add(id);
    if (r.typeName === "shape") {
      add(r.parentId, depth + 1);
      if ("assetId" in r.props && r.props.assetId)
        add(r.props.assetId, depth + 1);
    }
  };
  ids.forEach((id) => add(id));
  const records = Object.fromEntries(
    [...keep].map((id) => [id, store[id] ? recordVersion(store[id]) : null]),
  );
  const children = Object.fromEntries(
    [...keep]
      .filter((id) => store[id]?.typeName === "shape")
      .map((id) => [id, recordVersion(members(store, id))]),
  );
  return {
    records,
    children,
    pages: recordVersion(
      Object.values(store)
        .filter((r) => r.typeName === "page")
        .sort((a, b) => a.id.localeCompare(b.id)),
    ),
  };
}
export function changedSummary(
  storage: RecordStorage<TLRecord>,
  since: number,
) {
  const changes = storage.changesSince(since);
  return {
    since,
    currentRevision: storage.getClock(),
    requiresFullRead: changes === null,
    truncated: (changes?.size ?? 0) > 100,
    changed: changes
      ? [...changes].slice(0, 100).map(([id, r]) => ({
          id,
          kind: r ? "changed" : "removed",
          type: r?.typeName,
          shapeType: r?.typeName === "shape" ? r.type : undefined,
          parentId: r?.typeName === "shape" ? r.parentId : undefined,
          name:
            r?.typeName === "page"
              ? r.name
              : r?.typeName === "shape" && "name" in r.props
                ? r.props.name
                : undefined,
        }))
      : [],
  };
}
export function preconditionConflicts(
  txn: Transaction<TLRecord>,
  conditions: Preconditions,
  operations: unknown[],
) {
  const store = Object.fromEntries(txn.entries()),
    required = new Set<string>();
  const add = (id: unknown, depth = 0) => {
    if (typeof id !== "string" || depth > 32 || required.has(id)) return;
    const r = store[id];
    if (!r) return;
    required.add(id);
    if (r.typeName === "shape") {
      add(r.parentId, depth + 1);
      if ("assetId" in r.props) add(r.props.assetId, depth + 1);
      if (r.type === "group")
        for (const child of Object.values(store))
          if (child.typeName === "shape" && child.parentId === r.id)
            add(child.id, depth + 1);
    }
  };
  for (const raw of operations) {
    const op = raw as Record<string, unknown>;
    for (const key of [
      "id",
      "sheetId",
      "pageId",
      "assetId",
      "referenceId",
      "beforePageId",
    ])
      add(op[key]);
    if (Array.isArray(op.ids)) op.ids.forEach((id) => add(id));
    if (op.anchor && typeof op.anchor === "object") {
      const a = op.anchor as Record<string, unknown>;
      add(a.shapeId);
      add(a.pageId);
    }
  }
  const conflicts: { id: string; reason: string }[] = [];
  for (const id of required) {
    if (!(id in conditions.records))
      conflicts.push({ id, reason: "missing precondition" });
    const r = store[id];
    if (r.typeName === "shape" && !conditions.children?.[id])
      conflicts.push({ id, reason: "missing contents precondition" });
  }
  for (const [id, expected] of Object.entries(conditions.records))
    if ((store[id] ? recordVersion(store[id]) : null) !== expected)
      conflicts.push({ id, reason: "record changed" });
  for (const [id, expected] of Object.entries(conditions.children ?? {})) {
    const current = recordVersion(members(store, id));
    if (current !== expected)
      conflicts.push({ id, reason: "contents changed" });
  }
  if (
    conditions.pages &&
    conditions.pages !== readPreconditions(store, []).pages
  )
    conflicts.push({ id: "pages", reason: "page organization changed" });
  for (const op of operations as { type: string }[])
    if (
      ["add_page", "update_page", "move_sheet"].includes(op.type) &&
      !conditions.pages
    )
      conflicts.push({ id: "pages", reason: "missing page precondition" });
  return conflicts.slice(0, 100);
}

export type ConflictDetails = ReturnType<typeof changedSummary> & {
  expectedRevision: number;
  conflicts?: { id: string; reason: string }[];
};
